// import components
import { Fragment } from 'react';
import { Box } from '@mui/material';
import OpenCalendarIcon from './components/OpenCalendarIcon';
import type { EntityInfoRow } from 'components/shared/EntityInfoTable';
import { EntityInfoTable } from 'components/shared/EntityInfoTable';

// import enums
import { EntityType } from 'components/mrp/mrp.consts';

// import hooks
import { useTranslation } from 'react-i18next';

// import types
import { SummaryCardProps } from './SummaryCard.types';

// import utils
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { timeFormatter } from 'utils/grid';
import { formatDateTime } from 'utils/date';
import { PlanningEntityIdentifier } from 'generated/index';
import { groupStatusByDestination, toLinks } from './SummaryCard.utils';
import { getIconColor, getIconType } from 'components/mrp/mrp.utils';

// import styles
import {
  StyledBoxFlexCenterSmallGap,
  StyledBoxFlexColumnSmallGap
} from 'components/shared/Box/Box.styles';
import { StyledSummaryCardLabel } from './SummaryCard.styles';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useShallow } from 'zustand/shallow';

dayjs.extend(duration);

const SummaryCard = (props: SummaryCardProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });
  const { event, onMouseOver, interactable = true } = props;
  const { start: startTime, end: endTime, planningEvent } = event;
  const {
    activity,
    clients,
    date,
    notePublic,
    noteInternal,
    durationInSeconds,
    employees,
    resources,
    workPlace,
    isRecurring,
    recurrenceRuleRepresentation,
    endDateAppointmentSeries,
    hasFutureSubSeriesOrExceptions,
    synchronizationStatusSummary,
    importSyncStatus,
    externalId,
    createdById,
    createdByName,
    createdOn,
    lastUpdatedById,
    lastUpdatedByName,
    lastUpdatedOn
  } = planningEvent;

  const createdByLabel = createdByName ?? createdById;
  const lastUpdatedByLabel = lastUpdatedByName ?? lastUpdatedById;

  const { calendarIdsPerEntityId } = useCalendarStore(
    useShallow((state) => ({
      calendarIdsPerEntityId: state.calendarIdsPerEntityId
    }))
  );

  const isActive = (pei: PlanningEntityIdentifier) =>
    pei &&
    Object.keys(calendarIdsPerEntityId).some(
      (activeEntity) =>
        activeEntity === pei.entityId || activeEntity === pei.planningEntityType
    );

  // what and where section
  const activityName = activity?.name?.trim();
  const activityCode = activity?.code?.trim();
  const activityPlenary =
    activityName && activityCode
      ? `${activityName} - ${activityCode}`
      : activityName ?? activityCode;
  const workplaceLink = toLinks(workPlace, EntityType.WORKPLACE);

  const whatAndWhere =
    activityPlenary && workplaceLink ? (
      <>
        <Box>{activityPlenary}</Box>
        <StyledBoxFlexCenterSmallGap>
          {workplaceLink.displayLabel}
          {!isActive(workplaceLink.planningEntityIdentifier) && (
            <OpenCalendarIcon entity={workplaceLink} />
          )}
        </StyledBoxFlexCenterSmallGap>
      </>
    ) : (
      activityPlenary || workplaceLink?.displayLabel
    );

  // when section
  const timeWindow =
    startTime && endTime
      ? `${timeFormatter(startTime)} - ${timeFormatter(endTime)}`
      : null;
  const formattedDuration = dayjs
    .duration(durationInSeconds, 'seconds')
    .format('HH:mm');
  const activityDate = date ? dayjs(date).format('DD-MM-YYYY') : null;
  const recurrenceSummary = recurrenceRuleRepresentation?.replace(
    /\s+(tot|until)\s+\d{2}-\d{2}-\d{4}$/i,
    ''
  );
  const dateAndTime =
    timeWindow && activityDate ? (
      <>
        <Box>{activityDate}</Box>
        <Box>
          {timeWindow}
          {formattedDuration && ` (${formattedDuration})`}
        </Box>
        {isRecurring && <Box>{recurrenceSummary}</Box>}
        {isRecurring && endDateAppointmentSeries && (
          <Box>
            {t('appointmentForm.seriesEnd')}:{' '}
            {dayjs(endDateAppointmentSeries).format('DD-MM-YYYY')}
          </Box>
        )}
        {isRecurring && hasFutureSubSeriesOrExceptions && (
          <Box>{t('appointmentForm.FutureChanges')}</Box>
        )}
      </>
    ) : (
      activityDate || timeWindow
    );

  const synchronizationStatusEntries = Object.entries(
    groupStatusByDestination(synchronizationStatusSummary || {})
  );

  const synchronizationStatus = synchronizationStatusSummary ? (
    <StyledBoxFlexColumnSmallGap>
      {synchronizationStatusEntries.length > 0 ? (
        synchronizationStatusEntries.map(([destination, statusData]) => {
          const {
            status,
            syncDateTime,
            errorCode,
            errorMessage,
            stale,
            wontSync,
            missingCalculation,
            missingExport
          } = statusData;

          const statusLabel = wontSync
            ? t('synchronisationStatus.status.WONT_SYNC')
            : missingCalculation
            ? t('synchronisationStatus.status.MISSING_CALCULATION')
            : missingExport
            ? t('synchronisationStatus.status.MISSING_EXPORT')
            : t(`synchronisationStatus.status.${status}`);

          const staleIndicator =
            stale && !wontSync && !missingCalculation && !missingExport;

          return (
            <Box key={destination}>
              <StyledSummaryCardLabel>{destination}</StyledSummaryCardLabel>
              <Fragment>
                <>
                  {statusLabel}
                  {staleIndicator &&
                    ` (${t('synchronisationStatus.status.STALE')})`}
                  {syncDateTime && ` - ${formatDateTime(syncDateTime, true)}`}
                </>

                {(errorCode || errorMessage) && (
                  <Box>
                    {[errorCode, errorMessage].filter(Boolean).join(' - ')}
                  </Box>
                )}
              </Fragment>
            </Box>
          );
        })
      ) : (
        /* Text shown when there are no entries at all */
        <Box>
          {t('synchronisationStatus.synchronizationStatusNotAvailable')}
        </Box>
      )}
    </StyledBoxFlexColumnSmallGap>
  ) : null;

  const importSynchronisationStatus = externalId ? (
    <StyledBoxFlexColumnSmallGap>
      {importSyncStatus ? (
        <>
          <Box>
            {t(`synchronisationStatus.status.${importSyncStatus.status}`)}
            {importSyncStatus.lastSyncedAt &&
              ` - ${formatDateTime(importSyncStatus.lastSyncedAt, true)}`}
            {` (${externalId})`}
          </Box>
          {importSyncStatus.validationMessages && (
            <Box>{importSyncStatus.validationMessages}</Box>
          )}
        </>
      ) : (
        <Box>{t('importSynchronisationStatus.notAvailable')}</Box>
      )}
    </StyledBoxFlexColumnSmallGap>
  ) : null;

  const summaryCardData: EntityInfoRow[] = [
    {
      label: t('appointmentForm.when'),
      value: dateAndTime,
      icon: {
        type: 'calendar'
      }
    },
    {
      label: t('appointmentForm.whatAndWhere'),
      value: whatAndWhere,
      icon: {
        type: getIconType(EntityType.WORKPLACE),
        color: getIconColor(EntityType.WORKPLACE)
      }
    },
    {
      label: t('entityTypeMap.EMPLOYEE.plural'),
      value:
        employees?.length > 0 &&
        toLinks(employees, EntityType.EMPLOYEE)?.map((val, index) => (
          <StyledBoxFlexCenterSmallGap key={index}>
            {val.displayLabel}
            {interactable && !isActive(val.planningEntityIdentifier) && (
              <OpenCalendarIcon entity={val} />
            )}
          </StyledBoxFlexCenterSmallGap>
        )),
      icon: {
        type: getIconType(EntityType.EMPLOYEE),
        color: getIconColor(EntityType.EMPLOYEE)
      },
      useListCell: true
    },
    {
      label: t('appointmentForm.clients'),
      value:
        clients?.length > 0 &&
        toLinks(clients, EntityType.CLIENT)?.map((val, index) => (
          <StyledBoxFlexCenterSmallGap key={index}>
            {val.displayLabel}
            {interactable && !isActive(val.planningEntityIdentifier) && (
              <OpenCalendarIcon entity={val} />
            )}
          </StyledBoxFlexCenterSmallGap>
        )),
      icon: {
        type: getIconType(EntityType.CLIENT),
        color: getIconColor(EntityType.CLIENT)
      },
      useListCell: true
    },
    {
      label: t('appointmentForm.resources'),
      value:
        resources &&
        resources?.length > 0 &&
        toLinks(resources, EntityType.RESOURCE)?.map((val, index) => (
          <StyledBoxFlexCenterSmallGap key={index}>
            {val.displayLabel}
            {interactable && !isActive(val.planningEntityIdentifier) && (
              <OpenCalendarIcon entity={val} />
            )}
          </StyledBoxFlexCenterSmallGap>
        )),
      icon: {
        type: getIconType(EntityType.RESOURCE),
        color: getIconColor(EntityType.RESOURCE)
      },
      useListCell: true
    },
    {
      label: t('appointmentForm.notePublic'),
      value: notePublic,
      icon: {
        type: 'note'
      }
    },
    {
      label: t('appointmentForm.noteInternal'),
      value: noteInternal,
      icon: {
        type: 'note'
      }
    },
    {
      label: t('synchronisationStatus.synchronizationStatus'),
      value: synchronizationStatus,
      icon: {
        type: 'arrow-right-to-line'
      }
    },
    {
      label: t('importSynchronisationStatus.label'),
      value: importSynchronisationStatus,
      icon: {
        type: 'arrow-left-to-line'
      }
    },
    {
      label: t('appointmentForm.audit'),
      value: (createdOn || lastUpdatedOn) && (
        <StyledBoxFlexColumnSmallGap>
          {createdOn && (
            <Box>
              {t('appointmentForm.createdOn')}: {formatDateTime(createdOn)}
              {createdByLabel != null && ` (${createdByLabel})`}
            </Box>
          )}
          {lastUpdatedOn && (
            <Box>
              {t('appointmentForm.lastUpdatedOn')}:{' '}
              {formatDateTime(lastUpdatedOn)}
              {lastUpdatedByLabel != null && ` (${lastUpdatedByLabel})`}
            </Box>
          )}
        </StyledBoxFlexColumnSmallGap>
      ),
      icon: {
        type: 'clock'
      }
    }
  ];

  return <EntityInfoTable rows={summaryCardData} onMouseOver={onMouseOver} />;
};

export default SummaryCard;
